import { TagsInput, type TagsInputProps } from '@mantine/core'
import { formatArea, type ThaiArea } from '@tnn/core'
import { PickInput, type PickInputProps, type PickOption } from '@tnn/ui'
import { useState } from 'react'
import type { CustomFieldProps } from '../resource/config'
import { useFieldLabel } from '../resource/FieldInput'
import { type Area, searchAreas, thaiAreas } from './areas'

const loadAreas = async (query: string): Promise<PickOption<Area>[]> =>
  (await searchAreas(query)).map((a) => ({
    value: `${a.subdistrict}|${a.district}|${a.province}|${a.postcode}`,
    label: formatArea({ subdistrict: a.subdistrict, district: a.district }),
    description: formatArea({ province: a.province, postcode: a.postcode }),
    item: a,
  }))

export interface AreaInputProps extends Omit<PickInputProps<Area>, 'value' | 'onChange' | 'load' | 'onPick'> {
  value: ThaiArea
  onChange: (area: ThaiArea) => void
}

/**
 * One box for subdistrict, district, province and postcode: type any of them
 * and pick the place, so the four always agree and the postcode is never typed.
 */
export function AreaInput({ value, onChange, ...rest }: AreaInputProps) {
  // What is being typed; null shows the chosen place
  const [query, setQuery] = useState<string | null>(null)
  const shown = formatArea(value)
  return (
    <PickInput
      {...rest}
      value={query ?? shown}
      load={loadAreas}
      onChange={(q) => {
        setQuery(q)
        if (!q.trim()) onChange({ subdistrict: '', district: '', province: '', postcode: '' })
      }}
      onPick={(o) => {
        onChange(o.item)
        setQuery(null)
      }}
      onFocus={(e) => {
        setQuery(shown)
        e.currentTarget.select()
        rest.onFocus?.(e)
      }}
      onBlur={(e) => {
        setQuery(null)
        rest.onBlur?.(e)
      }}
    />
  )
}

const areaKeys = ['subdistrict', 'district', 'province', 'postcode'] as const

export interface AreaFieldProps extends CustomFieldProps {
  /** Field names: none → subdistrict, district…; `sender` → senderSubdistrict, senderDistrict… */
  prefix?: string
  /** Runs after a new place is set (e.g. a waybill re-picks its destination branch) */
  onAreaChange?: (form: CustomFieldProps['form']) => void
}

/**
 * Form field for a party's area, stored in four fields. Register it on the
 * province field (`form.custom`) and hide the other three (`form.hidden`):
 * `customFields: { senderProvince: (p) => <AreaField {...p} prefix="sender" /> }`.
 */
export function AreaField({ form, readOnly, t, field, prefix = '', onAreaChange }: AreaFieldProps) {
  const name = (key: (typeof areaKeys)[number]) => (prefix ? `${prefix}${key[0]?.toUpperCase()}${key.slice(1)}` : key)
  const value = Object.fromEntries(areaKeys.map((k) => [k, form.values[name(k)] as string | null])) as ThaiArea
  const error = areaKeys.map((k) => form.errors[name(k)]).find(Boolean)
  const labelling = useFieldLabel()
  return (
    <AreaInput
      {...labelling}
      label={t('form.area')}
      placeholder={t('form.areaHint')}
      withAsterisk={field.required && !readOnly}
      readOnly={readOnly}
      error={error}
      value={value}
      onChange={(area) => {
        form.setValues(Object.fromEntries(areaKeys.map((k) => [name(k), area[k] ?? ''])))
        form.clearFieldError(name('province'))
        onAreaChange?.(form)
      }}
    />
  )
}

let coverageList: Promise<string[]> | undefined
let placeList: Promise<string[]> | undefined

/** Provinces, then "province/district" pairs: what a branch can say it delivers to */
function coverageOptions(): Promise<string[]> {
  coverageList ??= thaiAreas().then((rows) => [
    ...new Set(rows.map((r) => r.province)),
    ...new Set(rows.map((r) => `${r.province}/${r.district}`)),
  ])
  return coverageList
}

/** "province/district" pairs, then "province/district/subdistrict": the places a branch can leave out of its area */
function placeOptions(): Promise<string[]> {
  placeList ??= thaiAreas().then((rows) => [
    ...new Set(rows.map((r) => `${r.province}/${r.district}`)),
    ...new Set(rows.map((r) => `${r.province}/${r.district}/${r.subdistrict}`)),
  ])
  return placeList
}

/** Given `within` (a branch's coverage), it offers only the places inside it, for those the branch does not deliver to */
export function CoverageInput({ onFocus, within, ...rest }: Omit<TagsInputProps, 'data'> & { within?: readonly string[] }) {
  const [data, setData] = useState<string[]>([])
  const options = () =>
    within ? placeOptions().then((all) => all.filter((place) => within.some((area) => place.startsWith(`${area}/`)))) : coverageOptions()
  return (
    <TagsInput
      {...rest}
      data={data}
      limit={20}
      onFocus={(e) => {
        options().then(setData)
        onFocus?.(e)
      }}
    />
  )
}

/**
 * Form field for a branch's delivery area (provinces or single districts), or, given `within` (its coverage), the
 * districts and subdistricts inside it the branch does not deliver to
 */
export function CoverageField({
  form,
  readOnly,
  t,
  field,
  description,
  within,
}: CustomFieldProps & { description?: string; within?: readonly string[] }) {
  const input = form.getInputProps(field.name)
  const labelling = useFieldLabel()
  return (
    <CoverageInput
      {...labelling}
      label={t(field.label)}
      description={readOnly ? undefined : description}
      placeholder={readOnly ? undefined : t(within ? 'form.excludedHint' : 'form.coverageHint')}
      readOnly={readOnly}
      error={input.error}
      value={(input.value as string[] | null) ?? []}
      onChange={input.onChange}
      within={within}
    />
  )
}
